import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations as fallbackTranslations } from '../utils/translations';
import { useLanguage } from './LanguageContext';

const TranslationContext = createContext({});

export const useTranslations = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslations must be used within TranslationProvider');
  }
  return context;
};

const STORAGE_KEY = 'poc_translations';

/**
 * Deep-merge override translations onto base translations.
 * A shallow spread would replace entire namespaces (e.g. all of
 * `purchase.*`) with the sparse localStorage overrides, wiping out
 * bundled translations for every key not individually overridden.
 */
function deepMergeTranslations(base, override) {
  if (!override) return base;
  const result = { ...base };
  for (const key of Object.keys(override)) {
    const baseVal = result[key];
    const overrideVal = override[key];
    if (
      baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal) &&
      overrideVal && typeof overrideVal === 'object' && !Array.isArray(overrideVal)
    ) {
      result[key] = deepMergeTranslations(baseVal, overrideVal);
    } else {
      result[key] = overrideVal;
    }
  }
  return result;
}

export function TranslationProvider({ children }) {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState(fallbackTranslations);
  const [rawTranslations, setRawTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTranslations = () => {
    try {
      setLoading(true);
      const stored = localStorage.getItem(STORAGE_KEY);

      if (stored) {
        const parsed = JSON.parse(stored);
        setRawTranslations(parsed);

        const translationMap = {};
        parsed.forEach((item) => {
          if (!translationMap[item.language]) {
            translationMap[item.language] = {};
          }

          const keys = item.key.split('.');
          let current = translationMap[item.language];

          for (let i = 0; i < keys.length - 1; i++) {
            if (!current[keys[i]]) {
              current[keys[i]] = {};
            }
            current = current[keys[i]];
          }

          current[keys[keys.length - 1]] = item.value;
        });

        setTranslations({
          en: deepMergeTranslations(fallbackTranslations.en, translationMap.en),
          fr: deepMergeTranslations(fallbackTranslations.fr, translationMap.fr),
          es: deepMergeTranslations(fallbackTranslations.es, translationMap.es)
        });
      } else {
        setTranslations(fallbackTranslations);
      }

      setError(null);
    } catch (err) {
      console.error('Error loading translations:', err);
      setError(err.message);
      setTranslations(fallbackTranslations);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTranslations();
  }, []);

  const t = (key, fallback) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return fallback || key;
      }
    }

    return value || fallback || key;
  };

  const refreshTranslations = async () => {
    loadTranslations();
  };

  const updateTranslation = async (key, value, lang = language) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const existing = stored ? JSON.parse(stored) : [];

      const index = existing.findIndex(t => t.key === key && t.language === lang);
      const newEntry = { key, language: lang, value, category: key.split('.')[0] };

      if (index >= 0) {
        existing[index] = newEntry;
      } else {
        existing.push(newEntry);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      loadTranslations();
      return { success: true };
    } catch (err) {
      console.error('Error updating translation:', err);
      return { success: false, error: err.message };
    }
  };

  const bulkUpdateTranslations = async (updates) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const existing = stored ? JSON.parse(stored) : [];

      updates.forEach(({ key, value, language: lang }) => {
        const targetLang = lang || language;
        const index = existing.findIndex(t => t.key === key && t.language === targetLang);
        const newEntry = { key, language: targetLang, value, category: key.split('.')[0] };

        if (index >= 0) {
          existing[index] = newEntry;
        } else {
          existing.push(newEntry);
        }
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      loadTranslations();
      return { success: true };
    } catch (err) {
      console.error('Error bulk updating translations:', err);
      return { success: false, error: err.message };
    }
  };

  const value = {
    t,
    translations,
    rawTranslations,
    loading,
    error,
    refreshTranslations,
    updateTranslation,
    bulkUpdateTranslations,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}
